import { getLocalTimeZone, today } from '@internationalized/date';
import { useControlledState } from '@react-stately/utils';
import { useDateFormatter } from 'react-aria';
import {
  DatePicker as AriaDatePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover,
} from 'react-aria-components';

import { CalendarIcon } from '../../../icons';
import { cx } from '../../../utils/cx';
import Button from '../buttons/Button';

import { Calendar } from './Calendar';

import type { DatePickerProps as AriaDatePickerProps, DateValue } from 'react-aria-components';

const highlightedDates = [today(getLocalTimeZone())];

interface IDatePickerProps extends AriaDatePickerProps<DateValue> {
  /** The function to call when the apply button is clicked. */
  onApply?: () => void;
  /** The function to call when the cancel button is clicked. */
  onCancel?: () => void;
}

export const DatePicker = ({
  value: valueProp,
  defaultValue,
  onChange,
  onApply,
  onCancel,
  ...props
}: IDatePickerProps) => {
  const formatter = useDateFormatter({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const [value, setValue] = useControlledState(valueProp, defaultValue || null, onChange);

  const formattedDate = value ? formatter.format(value.toDate(getLocalTimeZone())) : 'Select date';

  return (
    <AriaDatePicker shouldCloseOnSelect={false} {...props} value={value} onChange={setValue}>
      <AriaGroup>
        <Button size="md" color="secondary" iconLeading={CalendarIcon}>
          {formattedDate}
        </Button>
      </AriaGroup>
      <AriaPopover
        offset={8}
        placement="bottom right"
        className={({ isEntering, isExiting }) =>
          cx(
            'origin-(--trigger-anchor-point) will-change-transform',
            isEntering &&
              'animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5 duration-150 ease-out',
            isExiting &&
              'animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5 duration-100 ease-in'
          )
        }
      >
        <AriaDialog className="bg-primary ring-secondary_alt rounded-2xl shadow-xl ring">
          {({ close }) => (
            <>
              <div className="flex px-6 py-5">
                <Calendar highlightedDates={highlightedDates} />
              </div>
              <div className="border-secondary grid grid-cols-2 gap-3 border-t p-4">
                <Button
                  size="md"
                  color="secondary"
                  onClick={() => {
                    onCancel?.();
                    close();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  color="primary"
                  onClick={() => {
                    onApply?.();
                    close();
                  }}
                >
                  Apply
                </Button>
              </div>
            </>
          )}
        </AriaDialog>
      </AriaPopover>
    </AriaDatePicker>
  );
};
